'use client'
import { Decision } from '@/app/api/placements/types'
import NavigateRightIcon from '@/app/icons/navigate-right'
import Attribution from '@/components/attribution-v2'
import UAParserContext from '@/context/ua-parser'
import { fireAdTrackingPixel, handleAdCtaClick, useAdMediaClick } from '@/util/ad-click'
import { getGifHeight, getGifWidth } from '@giphy/js-util'
import { Gif, GifOverlayProps } from '@giphy/react-components'
import { useContext } from 'react'
import ScrollCarousel from '../scroll-carousel'
import { spotlightWidth } from './spotlight-util'

const GridOverlay = ({ gif, carouselData }: GifOverlayProps & { carouselData: Decision }) => {
    return (
        <div className="absolute inset-0">
            {gif.user && <Attribution user={gif.user} className="absolute bottom-2 left-2" />}
            {carouselData?.cta_url && (
                <div
                    className="absolute right-2 bottom-2 flex h-[50px] items-center justify-center gap-2 rounded-md bg-giphyBlack/50 px-5 font-bold text-giphyWhite hover:bg-giphyBlack/80"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleAdCtaClick({
                            ctaTrackingUrl: carouselData.cta_tracking_url,
                            ctaUrl: carouselData.cta_url,
                        })
                    }}
                >
                    {carouselData.cta_text} <NavigateRightIcon className="size-3 fill-giphyWhite" />
                </div>
            )}
        </div>
    )
}
const MainGif = ({ data, width }: { data: Decision; width: number; height?: number }) => {
    const { impression_tracking_url, click_tracking_url, click_url, gif, third_party_tag } = data
    const onAdMediaClick = useAdMediaClick()
    if (!gif) return null
    const aspectRatio = gif.images.original.width / gif.images.original.height
    return (
        <Gif
            gif={gif}
            width={width}
            percentWidth="100%"
            overlay={(props) => <GridOverlay {...props} carouselData={data} />}
            style={{ aspectRatio }}
            lazyLoad={false}
            noLink
            // eslint-disable-next-line better-tailwindcss/no-unregistered-classes
            className="giphy-spotlight-grid"
            onGifSeen={() => {
                try {
                    fireAdTrackingPixel(impression_tracking_url)
                    fireAdTrackingPixel(third_party_tag)
                } catch (_e) {}
            }}
            onGifClick={(_, event) => {
                event?.preventDefault()
                event?.stopPropagation()
                onAdMediaClick({
                    clickTrackingUrl: click_tracking_url,
                    clickUrl: click_url,
                    gifUrl: gif.url,
                })
            }}
        />
    )
}

const CarouselGif = ({ data, height }: { data: Decision; height: number }) => {
    const { impression_tracking_url, click_tracking_url, click_url, gif, third_party_tag } = data
    const onAdMediaClick = useAdMediaClick()
    if (!gif) return null
    return (
        <Gif
            gif={gif}
            key={gif.id}
            height={height}
            width={getGifWidth(gif, height)}
            className="shrink-0"
            hideAttribution
            noLink
            onGifClick={(_, event) => {
                event?.preventDefault()
                event?.stopPropagation()
                onAdMediaClick({
                    clickTrackingUrl: click_tracking_url,
                    clickUrl: click_url,
                    gifUrl: gif.url,
                })
            }}
            onGifSeen={() => {
                try {
                    fireAdTrackingPixel(impression_tracking_url)
                    fireAdTrackingPixel(third_party_tag)
                } catch (_e) {}
            }}
        />
    )
}

const carouselHeight = 124
const carouselId = 'giphy-xl-banner-carousel'

const SpotlightGrid = ({ data }: { data: Decision }) => {
    const { deviceType } = useContext(UAParserContext)
    const { gif } = data
    if (!gif) return null
    return (
        <>
            <MainGif key={gif.id} data={data} width={spotlightWidth} height={getGifHeight(gif, spotlightWidth)} />
            <ScrollCarousel
                height={carouselHeight}
                width={spotlightWidth}
                elementId={carouselId}
                deviceType={deviceType}
            >
                {data.subdecisions?.map((decision) => (
                    <CarouselGif key={decision.gif?.id} data={decision} height={carouselHeight} />
                ))}
            </ScrollCarousel>
        </>
    )
}

export default SpotlightGrid
